import React, { useEffect, useState } from "react";
import styled from "styled-components";
import {
  FiLogOut,
  FiMenu,
  FiX,
  FiMessageSquare,
  FiGrid,
} from "react-icons/fi";
import { logout } from "../store/slices/auth/asyncThunks";
import { useDispatch } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { BiBox, BiCategory } from "react-icons/bi";
import { CgShoppingBag } from "react-icons/cg";
import { FaTicket } from "react-icons/fa6";
import { BiPackage } from "react-icons/bi";
import { getCategories } from "../store/slices/products/asyncThunks";

import Wordmark from "../assets/images/obsessed.png";

const sidebarLinks = [
  {
    label: "Dashboard",
    path: "/kontrolna-tabla/dashboard",
    icon: <FiGrid size={17} />,
    match: (pathname) => pathname === "/kontrolna-tabla/dashboard",
  },
  {
    label: "Proizvodi",
    path: "/kontrolna-tabla",
    icon: <BiBox size={17} />,
    match: (pathname) =>
      pathname === "/kontrolna-tabla" || pathname.includes("edit"),
  },
  {
    label: "Kategorije",
    path: "/kontrolna-tabla/kategorije",
    icon: <BiCategory size={17} />,
    match: (pathname) => pathname === "/kontrolna-tabla/kategorije",
  },
  {
    label: "Kuponi",
    path: "/kontrolna-tabla/kuponi",
    icon: <FaTicket size={17} />,
    match: (pathname) => pathname === "/kontrolna-tabla/kuponi",
  },
  {
    label: "Bundlovi",
    path: "/kontrolna-tabla/bundlovi",
    icon: <BiPackage size={17} />,
    match: (pathname) => pathname === "/kontrolna-tabla/bundlovi",
  },
  {
    label: "Porudžbine",
    path: "/kontrolna-tabla/porudzbine",
    icon: <CgShoppingBag size={17} />,
    match: (pathname) => pathname === "/kontrolna-tabla/porudzbine",
  },
  {
    label: "Poruke",
    path: "/kontrolna-tabla/poruke",
    icon: <FiMessageSquare size={17} />,
    match: (pathname) => pathname === "/kontrolna-tabla/poruke",
  },
];

const AdminContainer = ({ children }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const toggleSidebar = () => {
    setMobileOpen((prev) => !prev);
  };

  const handleNavigate = (path) => {
    navigate(path);
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() =>
        dispatch(getCategories())
          .unwrap()
          .then(() => {})
          .catch((err) => console.log(err.message))
      )
      .catch((err) => console.log(err.message));
  };

  useEffect(() => {
    if (mobileOpen) {
      document.documentElement.style.overflow = "hidden";
    } else {
      document.documentElement.style.overflow = "auto";
    }
  }, [mobileOpen]);

  const nav = (
    <>
      <Nav>
        {sidebarLinks.map((item) => (
          <SidebarItem
            key={item.label}
            $active={item.match(pathname)}
            onClick={() => handleNavigate(item.path)}
          >
            {item.icon}
            {item.label}
          </SidebarItem>
        ))}
      </Nav>

      <Logout onClick={handleLogout}>
        <FiLogOut size={16} />
        Izloguj se
      </Logout>
    </>
  );

  return (
    <Container>
      <MobileToggle onClick={toggleSidebar}>
        {mobileOpen ? <FiX size={20} /> : <FiMenu size={20} />}
      </MobileToggle>

      <Aside>
        <AsideLogo onClick={() => navigate("/")}>
          <span className="k">改善</span>
          <img src={Wordmark} alt="OBSESSED" />
          <span className="pill">Admin</span>
        </AsideLogo>
        {nav}
      </Aside>

      <Main>{children}</Main>

      <AnimatePresence>
        {mobileOpen && (
          <MobileOverlay
            as={motion.div}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setMobileOpen(false)}
          >
            <MobilePanel
              as={motion.div}
              initial={{ x: "-100%" }}
              animate={{ x: 0 }}
              exit={{ x: "-100%" }}
              transition={{ duration: 0.35, ease: [0.22, 0.72, 0.18, 1] }}
              onClick={(e) => e.stopPropagation()}
            >
              <AsideLogo onClick={() => navigate("/")}>
                <span className="k">改善</span>
                <img src={Wordmark} alt="OBSESSED" />
                <span className="pill">Admin</span>
              </AsideLogo>
              {nav}
            </MobilePanel>
          </MobileOverlay>
        )}
      </AnimatePresence>
    </Container>
  );
};

export default AdminContainer;

const Container = styled.div`
  display: flex;
  width: 100%;
  min-height: 100svh;
  background: var(--bg0, #0a0a0c);
  color: var(--text, #f4f2ee);
`;

const Aside = styled.aside`
  width: 22.5rem;
  flex-shrink: 0;
  position: sticky;
  top: 0;
  height: 100lvh;
  background: var(--bg1, #101013);
  border-right: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 2.6rem 1.4rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 910px) {
    display: none;
  }
`;

const AsideLogo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.8rem;
  padding: 0 0.6rem 2.6rem;
  cursor: pointer;
  min-width: 0;

  .k {
    font-family: var(--font-kanji);
    color: var(--red, #fe5f55);
    font-size: 1.4rem;
    letter-spacing: 0.1em;
    white-space: nowrap;
    flex-shrink: 0;
  }

  img {
    height: 1.5rem;
    width: auto;
    max-width: 11rem;
    object-fit: contain;
    flex-shrink: 1;
    min-width: 0;
  }

  .pill {
    margin-left: auto;
    flex-shrink: 0;
    font-size: 0.85rem;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    font-family: "Poppins-SemiBold";
    color: var(--muted, #8e8c94);
    border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
    padding: 0.3rem 0.8rem;
    border-radius: 10rem;
  }
`;

const Nav = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
`;

const SidebarItem = styled.button`
  background: ${({ $active }) =>
    $active ? "rgba(254, 95, 85, 0.1)" : "transparent"};
  color: ${({ $active }) =>
    $active ? "var(--text, #f4f2ee)" : "var(--muted, #8e8c94)"};
  padding: 1rem 1.2rem;
  border-radius: 0.4rem;
  border: none;
  border-left: 2px solid
    ${({ $active }) => ($active ? "var(--red, #fe5f55)" : "transparent")};
  text-align: left;
  font-size: 1.25rem;
  letter-spacing: 0.04em;
  font-family: ${({ $active }) =>
    $active ? '"Poppins-SemiBold"' : '"Poppins-Medium"'};
  cursor: pointer;
  transition: all 0.25s var(--ease, ease);
  display: flex;
  align-items: center;
  gap: 1.1rem;

  svg {
    color: ${({ $active }) =>
      $active ? "var(--red, #fe5f55)" : "var(--muted2, #5c5a63)"};
    transition: color 0.25s;
  }

  &:hover {
    background: ${({ $active }) =>
      $active ? "rgba(254, 95, 85, 0.1)" : "rgba(255, 255, 255, 0.03)"};
    color: var(--text, #f4f2ee);

    svg {
      color: var(--red, #fe5f55);
    }
  }
`;

const Logout = styled.button`
  background: transparent;
  cursor: pointer;
  border: none;
  border-top: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  outline: none;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 1.1rem;
  margin-top: auto;
  padding: 1.6rem 1.4rem 0.4rem;
  font-size: 1.3rem;
  letter-spacing: 0.04em;
  font-family: "Poppins-Medium";
  color: var(--muted2, #5c5a63);
  transition: color 0.25s;

  svg {
    color: var(--red, #fe5f55);
  }

  &:hover {
    color: var(--red, #fe5f55);
  }
`;

const Main = styled.main`
  flex: 1;
  min-width: 0;
  padding: 3.4rem clamp(1.8rem, 3vw, 4.4rem) 8rem;
  display: flex;
  flex-direction: column;
  gap: 1.4rem;

  @media (max-width: 910px) {
    padding-top: 7rem;
  }
`;

const MobileToggle = styled.button`
  display: none;
  background: rgba(16, 16, 19, 0.8);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid var(--line2, rgba(255, 255, 255, 0.14));
  color: var(--text, #f4f2ee);
  z-index: 1002;
  cursor: pointer;
  position: fixed;
  top: 1.6rem;
  right: 1.6rem;
  width: 4.2rem;
  height: 4.2rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.4rem;
  outline: none;
  transition: all 0.25s;

  &:hover {
    border-color: var(--red, #fe5f55);
    color: var(--red, #fe5f55);
  }

  @media (max-width: 910px) {
    display: flex;
  }
`;

const MobileOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(6, 6, 8, 0.7);
  z-index: 1000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const MobilePanel = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  bottom: 0;
  width: min(28rem, 84vw);
  background: var(--bg1, #101013);
  border-right: 1px solid var(--line, rgba(255, 255, 255, 0.07));
  padding: 2.6rem 1.4rem;
  z-index: 1001;
  display: flex;
  flex-direction: column;
`;
